/**
 * TilemapSurface — the framework-free paint surface (_docs/tilemap/02 §2–3, §6).
 * Owns the <canvas>, the camera, the chunk-bitmap cache, and the active tool.
 * Reads all editor state from tilemapStore (imperatively) and renders on demand
 * (no free-running rAF): a store change, camera move, or hover that changes the
 * target cell schedules exactly one rAF-coalesced redraw. Idle cost is zero.
 *
 * Rendering per frame: for each visible layer bottom-to-top, blit each visible
 * chunk's cached bitmap at the camera transform (culling off-screen chunks),
 * then the screen-space overlay (grid + cursor).
 */

import { tilemapStore, orderedLayers } from '../store/tilemapStore';
import { catalogStore } from '../store/catalogStore';
import { tilemapClient } from '../api/tilemapClient';
import { currentFrameAtlasIndex, isTileSolid, tileAnimationFor } from '../model/tileMeta';
import { Stroke } from '../store/strokes';
import { ChunkRenderer } from './ChunkRenderer';
import { CollisionCache } from './CollisionCache';
import { TilesetImage } from './tileset';
import { drawOverlay } from './overlay';
import { bresenham, paintStampStep } from './tools/paint';
import { fillRegion } from './tools/fill';
import { fillRect, pickRect } from './tools/rect';
import { applyAutotile, buildTerrainLookup } from '../model/autotile';
import type { LayerTiles } from '../model/chunk';
import { CHUNK_SIZE } from '../model/constants';
import type { Stamp } from '../model/types';
import {
    type Camera,
    panByScreen,
    screenToCell,
    visibleChunkRange,
    worldToScreen,
    zoomAt,
} from './camera';

export class TilemapSurface {
    private ctx: CanvasRenderingContext2D;
    private renderer = new ChunkRenderer();
    private collisionCache = new CollisionCache();
    private tilesets = new Map<string, TilesetImage>();
    private showCollisions = false;
    /** Shared clock for tile-animation frame selection (_docs/tilemap/07 roadmap). */
    private readonly animationStartMs = performance.now();

    private rafHandle = 0;
    private unsubscribe: (() => void) | null = null;
    private resizeObserver: ResizeObserver;

    // Interaction state
    private hoveredCell: { x: number; y: number } | null = null;
    private activeStroke: Stroke | null = null;
    private lastPaintedCell: { x: number; y: number } | null = null;
    private panning = false;
    private spaceDown = false;
    private altDown = false;
    private lastPointer = { x: 0, y: 0 };
    private showGrid = true;
    private showChunkDebug = false;

    /** Live drag rectangle for the Rect tool / Picker rect-drag (cell coords). */
    private dragRect: { start: { x: number; y: number }; end: { x: number; y: number }; erasing: boolean } | null = null;
    private dragMode: 'paint' | 'rect' | 'pick' | null = null;

    private lastRenderTick = -1;
    private lastStatus = '';
    private lastThumbAt = 0;
    private thumbBusy = false;

    constructor(private canvas: HTMLCanvasElement) {
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('2D canvas context unavailable');
        this.ctx = ctx;

        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(canvas);

        this.attachInput();
        this.resize();

        // Redraw when model data changes from outside the paint loop, and
        // refresh the map thumbnail after a successful save (throttled).
        this.unsubscribe = tilemapStore.subscribe((s) => {
            if (s.renderTick !== this.lastRenderTick) {
                this.lastRenderTick = s.renderTick;
                void this.ensureTilesetsLoaded();
                this.requestRender();
            }
            if (s.status !== this.lastStatus) {
                this.lastStatus = s.status;
                if (s.status === 'saved' && Date.now() - this.lastThumbAt > 15000) {
                    void this.captureThumbnail();
                }
            }
        });
        void this.ensureTilesetsLoaded();
        this.requestRender();
    }

    destroy(): void {
        this.resizeObserver.disconnect();
        this.unsubscribe?.();
        if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
        this.detachInput();
        this.renderer.clear();
    }

    setShowGrid(v: boolean): void {
        this.showGrid = v;
        this.requestRender();
    }

    setShowChunkDebug(v: boolean): void {
        this.showChunkDebug = v;
        this.requestRender();
    }

    setShowCollisions(v: boolean): void {
        this.showCollisions = v;
        this.requestRender();
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

    private requestRender(): void {
        if (this.rafHandle) return;
        this.rafHandle = requestAnimationFrame(() => {
            this.rafHandle = 0;
            this.render();
        });
    }

    private resize(): void {
        const dpr = window.devicePixelRatio || 1;
        const rect = this.canvas.getBoundingClientRect();
        this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
        this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
        this.requestRender();
    }

    private cssSize(): { w: number; h: number } {
        const dpr = window.devicePixelRatio || 1;
        return { w: this.canvas.width / dpr, h: this.canvas.height / dpr };
    }

    private async ensureTilesetsLoaded(): Promise<void> {
        const doc = tilemapStore.getState().doc;
        if (!doc) return;
        let loadedAny = false;
        for (const ts of doc.tilesets) {
            if (!this.tilesets.has(ts.uuid) && ts.texture_asset_uuid) {
                try {
                    this.tilesets.set(ts.uuid, await TilesetImage.load(ts));
                    loadedAny = true;
                } catch {
                    /* missing texture — chunks with this tileset render blank */
                }
            }
        }
        if (loadedAny) this.requestRender();
    }

    private render(): void {
        const state = tilemapStore.getState();
        const { doc, camera } = state;
        const dpr = window.devicePixelRatio || 1;
        const { w: cssW, h: cssH } = this.cssSize();

        const ctx = this.ctx;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // work in CSS pixels
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, cssW, cssH);

        if (!doc) return;

        const cellSize = doc.tilemap.cell_size;
        const chunkWorld = cellSize * CHUNK_SIZE;
        const collisionRects: Array<{ x: number; y: number; w: number; h: number }> = [];
        const elapsedMs = performance.now() - this.animationStartMs;
        let anyAnimatedVisible = false;

        for (const layer of orderedLayers(doc)) {
            if (!layer.visible) continue;
            const tiles = state.layerTiles.get(layer.uuid);
            const tileset = layer.tileset_uuid ? this.tilesets.get(layer.tileset_uuid) : undefined;
            if (!tiles || !tileset) continue;

            const range = visibleChunkRange(camera, cssW, cssH, cellSize, CHUNK_SIZE, 0);
            ctx.globalAlpha = layer.opacity;

            for (let cy = range.cy0; cy <= range.cy1; cy++) {
                for (let cx = range.cx0; cx <= range.cx1; cx++) {
                    const chunk = tiles.getChunk(cx, cy);
                    if (!chunk || chunk.usedCount === 0) continue;

                    // Capture dirtiness BEFORE bitmapFor clears the flag, so the
                    // collision cache re-meshes exactly when the bitmap rebuilds.
                    const wasDirty = chunk.renderDirty;
                    const bitmap = this.renderer.bitmapFor(layer.uuid, chunk, tileset);

                    // Chunk's world-space bottom-left → screen top-left (y flip).
                    const originWorldX = cx * chunkWorld;
                    const originWorldYTop = (cy + 1) * chunkWorld; // top edge in world
                    const tl = worldToScreen(camera, cssH, originWorldX, originWorldYTop);
                    const sizePx = chunkWorld * camera.zoom;
                    ctx.drawImage(bitmap, Math.round(tl.x), Math.round(tl.y), Math.ceil(sizePx), Math.ceil(sizePx));

                    // Animated cells were left transparent in the static bitmap
                    // (ChunkRenderer) — draw each one's current frame here,
                    // every tick, without touching the (expensive-to-rebuild)
                    // cached bitmap at all.
                    const animatedCells = this.renderer.animatedCellsFor(layer.uuid, cx, cy);
                    if (animatedCells.length > 0) {
                        anyAnimatedVisible = true;
                        const cellPx = cellSize * camera.zoom;
                        for (const cell of animatedCells) {
                            const animation = tileAnimationFor(tileset.meta, cell.tileId);
                            if (!animation) continue;
                            const atlasIndex = currentFrameAtlasIndex(animation, elapsedMs);
                            const src = tileset.tileRectPx(atlasIndex);
                            const worldCellX = cx * CHUNK_SIZE + cell.localX;
                            const worldCellY = cy * CHUNK_SIZE + cell.localY;
                            const cellTl = worldToScreen(camera, cssH, worldCellX * cellSize, (worldCellY + 1) * cellSize);
                            ctx.drawImage(tileset.image, src.x, src.y, src.w, src.h, Math.round(cellTl.x), Math.round(cellTl.y), Math.ceil(cellPx), Math.ceil(cellPx));
                        }
                    }

                    if (this.showCollisions && layer.generate_colliders) {
                        const rects = this.collisionCache.rectsFor(layer.uuid, chunk, wasDirty, (id) => isTileSolid(tileset.meta, id));
                        for (const r of rects) {
                            collisionRects.push({
                                x: cx * CHUNK_SIZE + r.x,
                                y: cy * CHUNK_SIZE + r.y,
                                w: r.w,
                                h: r.h,
                            });
                        }
                    }
                }
            }
            ctx.globalAlpha = 1;
        }

        drawOverlay(ctx, camera, cssW, cssH, {
            showGrid: this.showGrid,
            showChunkDebug: this.showChunkDebug,
            cellSize,
            hoveredCell: this.hoveredCell,
            stampW: state.stamp.w,
            stampH: state.stamp.h,
            brushSize: state.brushSize,
            dragRect: this.dragRect,
            collisionRects,
        });

        // Keep ticking ONLY while an animated tile is actually visible — idle
        // cost stays zero the moment nothing on screen animates (the same
        // "sync pass ~0 when nothing is dirty" principle the chunk cache uses).
        if (anyAnimatedVisible) this.requestRender();
    }

    // ── Input ─────────────────────────────────────────────────────────────────

    private onPointerDown = (e: PointerEvent) => {
        this.canvas.setPointerCapture(e.pointerId);
        this.lastPointer = { x: e.offsetX, y: e.offsetY };

        // Middle button or Space+drag = pan.
        if (e.button === 1 || this.spaceDown) {
            this.panning = true;
            return;
        }

        const state = tilemapStore.getState();
        const layer = state.doc?.layers.find((l) => l.uuid === state.activeLayerUuid);
        const cell = this.cellAt(e.offsetX, e.offsetY);

        // Alt+click (any tool) = eyedropper — allowed even on a locked layer
        // (reading, not writing). Picker tool likewise.
        if ((this.altDown || state.tool === 'picker') && cell) {
            this.dragMode = 'pick';
            this.dragRect = { start: cell, end: cell, erasing: false };
            this.requestRender();
            return;
        }

        if (!layer || layer.locked || !layer.visible) return; // refuse edits

        if (e.button !== 0 && e.button !== 2) return;
        if (state.tool === 'none' && e.button === 0) return; // neutral tool = no paint
        if (!cell) return;

        // Right button always erases (engine 46.3).
        const erasing = e.button === 2 || state.tool === 'erase';

        if (state.tool === 'rect') {
            this.dragMode = 'rect';
            this.dragRect = { start: cell, end: cell, erasing };
            this.requestRender();
            return;
        }

        if (state.tool === 'fill' && !erasing) {
            const tiles = state.activeTiles();
            if (tiles) {
                const stroke = new Stroke(layer.uuid);
                if (fillRegion(tiles, stroke, cell.x, cell.y, state.stamp)) {
                    state.commitStroke(stroke.freeze());
                    this.requestRender();
                }
            }
            return;
        }

        // Brush / Erase (and RMB-erase on any paint tool).
        this.dragMode = 'paint';
        this.activeStroke = new Stroke(layer.uuid);
        this.lastPaintedCell = null;
        this.paintAt(cell, erasing);
    };

    private onPointerMove = (e: PointerEvent) => {
        const state = tilemapStore.getState();
        const cell = this.cellAt(e.offsetX, e.offsetY);
        const hoverChanged = !this.hoveredCell || !cell || this.hoveredCell.x !== cell.x || this.hoveredCell.y !== cell.y;
        this.hoveredCell = cell;

        if (this.panning) {
            const cam = panByScreen(state.camera, e.offsetX - this.lastPointer.x, e.offsetY - this.lastPointer.y);
            this.lastPointer = { x: e.offsetX, y: e.offsetY };
            state.setCamera(cam);
            this.requestRender();
            return;
        }

        if (!cell) {
            if (hoverChanged) this.requestRender();
            return;
        }

        if (this.dragMode === 'paint' && this.activeStroke) {
            const erasing = e.buttons === 2 || state.tool === 'erase';
            this.paintAt(cell, erasing);
        } else if ((this.dragMode === 'rect' || this.dragMode === 'pick') && this.dragRect) {
            this.dragRect.end = cell;
            this.requestRender();
        } else if (hoverChanged) {
            this.requestRender();
        }
    };

    private onPointerUp = (e: PointerEvent) => {
        this.canvas.releasePointerCapture?.(e.pointerId);
        this.panning = false;
        const state = tilemapStore.getState();

        if (this.dragMode === 'paint' && this.activeStroke) {
            const frozen = this.activeStroke.freeze();
            if (frozen.edits.length > 0) state.commitStroke(frozen);
        } else if (this.dragMode === 'rect' && this.dragRect) {
            const tiles = state.activeTiles();
            const layerUuid = state.activeLayerUuid;
            if (tiles && layerUuid) {
                const { start, end, erasing } = this.dragRect;
                const stroke = new Stroke(layerUuid);
                if (fillRect(tiles, stroke, start.x, start.y, end.x, end.y, state.stamp, erasing)) {
                    state.commitStroke(stroke.freeze());
                }
            }
        } else if (this.dragMode === 'pick' && this.dragRect) {
            const tiles = state.activeTiles();
            if (tiles) {
                const { start, end } = this.dragRect;
                const stamp: Stamp =
                    start.x === end.x && start.y === end.y
                        ? { w: 1, h: 1, tiles: [tiles.getTile(start.x, start.y)] }
                        : pickRect(tiles, start.x, start.y, end.x, end.y);
                state.setStamp(stamp);
                if (state.tool === 'picker') state.setTool('brush'); // pick then paint
            }
        }

        this.activeStroke = null;
        this.lastPaintedCell = null;
        this.dragMode = null;
        this.dragRect = null;
        this.requestRender();
    };

    private onPointerLeave = () => {
        if (this.hoveredCell) {
            this.hoveredCell = null;
            this.requestRender();
        }
    };

    private onWheel = (e: WheelEvent) => {
        e.preventDefault();
        const { h: cssH } = this.cssSize();
        const state = tilemapStore.getState();
        const factor = Math.exp(-e.deltaY * 0.0015);
        const cam = zoomAt(state.camera, cssH, e.offsetX, e.offsetY, factor);
        state.setCamera(cam);
        this.requestRender();
    };

    private onContextMenu = (e: Event) => e.preventDefault(); // RMB paints/erases

    private onKeyDown = (e: KeyboardEvent) => {
        if (e.code === 'Space') this.spaceDown = true;
        if (e.key === 'Alt') this.altDown = true;
    };
    private onKeyUp = (e: KeyboardEvent) => {
        if (e.code === 'Space') this.spaceDown = false;
        if (e.key === 'Alt') this.altDown = false;
    };

    /** Screen pixel → integer cell, or null if no document is open. */
    private cellAt(sx: number, sy: number): { x: number; y: number } | null {
        const state = tilemapStore.getState();
        if (!state.doc) return null;
        return screenToCell(state.camera, this.cssSize().h, state.doc.tilemap.cell_size, sx, sy);
    }

    private paintAt(cell: { x: number; y: number }, erasing: boolean): void {
        const state = tilemapStore.getState();
        const tiles = state.activeTiles();
        if (!tiles || !this.activeStroke) return;

        if (state.tool === 'autotile') {
            this.paintAutotileAt(tiles, cell, erasing);
            return;
        }

        const eraseStamp: Stamp = { w: 1, h: 1, tiles: [0] };
        const stamp = erasing ? eraseStamp : state.stamp;
        const changed = paintStampStep(tiles, this.activeStroke, this.lastPaintedCell, cell, stamp, state.brushSize);
        this.lastPaintedCell = cell;
        if (changed) {
            state.markPainted();
            this.requestRender();
        }
    }

    /**
     * Autotile tool: paint/erase the active terrain along the Bresenham path
     * since the last sample, then let {@see applyAutotile} pick the correct
     * edge/corner variant for every touched cell and its terrain neighbors
     * (_docs/tilemap/07 roadmap). Each call only needs the NEW cells since the
     * last sample — `applyAutotile` recomputes neighbors from the persisted
     * `tiles` state, so incremental calls across a drag compose correctly.
     */
    private paintAutotileAt(tiles: LayerTiles, cell: { x: number; y: number }, erasing: boolean): void {
        const state = tilemapStore.getState();
        const terrain = state.activeTerrain;
        const tileset = state.activeTileset();
        if (!terrain || !tileset || !this.activeStroke) {
            this.lastPaintedCell = cell;
            return;
        }

        const path = this.lastPaintedCell ? bresenham(this.lastPaintedCell.x, this.lastPaintedCell.y, cell.x, cell.y) : [[cell.x, cell.y] as [number, number]];
        const touched = path.map(([x, y]) => ({ x, y, painted: !erasing }));
        const terrainOf = buildTerrainLookup(tileset.autotile_rules);
        const rules = tileset.autotile_rules?.[terrain]?.rules ?? {};

        const edits = applyAutotile(tiles, terrainOf, rules, terrain, touched);
        this.lastPaintedCell = cell;

        if (edits.length > 0) {
            for (const e of edits) this.activeStroke.record(e.x, e.y, e.before, e.after);
            state.markPainted();
            this.requestRender();
        }
    }

    /**
     * Render the painted bounds of all visible layers into a ≤512px PNG, upload
     * it as an asset, and set it as the map's thumbnail (_docs/tilemap/03 §5).
     * Client-side by design — a server tile renderer would be waste. Best-effort:
     * any failure is swallowed (a thumbnail is a hint, not truth).
     */
    async captureThumbnail(): Promise<void> {
        if (this.thumbBusy) return;
        const state = tilemapStore.getState();
        const { doc, projectUuid, tilemapUuid } = state;
        if (!doc || !projectUuid || !tilemapUuid) return;

        // Painted-chunk bounds across all layers.
        let has = false;
        let minCx = 0, minCy = 0, maxCx = 0, maxCy = 0;
        for (const layer of doc.layers) {
            const tiles = state.layerTiles.get(layer.uuid);
            if (!tiles) continue;
            for (const c of tiles.allChunks()) {
                if (!has) { minCx = maxCx = c.cx; minCy = maxCy = c.cy; has = true; }
                else {
                    minCx = Math.min(minCx, c.cx); maxCx = Math.max(maxCx, c.cx);
                    minCy = Math.min(minCy, c.cy); maxCy = Math.max(maxCy, c.cy);
                }
            }
        }
        if (!has) return; // nothing painted → no thumbnail

        const cellsW = (maxCx - minCx + 1) * CHUNK_SIZE;
        const cellsH = (maxCy - minCy + 1) * CHUNK_SIZE;
        const ppc = Math.min(4, 512 / Math.max(cellsW, cellsH)); // pixels per cell
        const outW = Math.max(1, Math.round(cellsW * ppc));
        const outH = Math.max(1, Math.round(cellsH * ppc));

        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.imageSmoothingEnabled = false;

        const chunkPx = CHUNK_SIZE * ppc;
        for (const layer of orderedLayers(doc)) {
            if (!layer.visible) continue;
            const tiles = state.layerTiles.get(layer.uuid);
            const tileset = layer.tileset_uuid ? this.tilesets.get(layer.tileset_uuid) : undefined;
            if (!tiles || !tileset) continue;
            ctx.globalAlpha = layer.opacity;
            for (const c of tiles.allChunks()) {
                const bitmap = this.renderer.bitmapFor(layer.uuid, c, tileset);
                const x = (c.cx - minCx) * chunkPx;
                // y-up chunk → y-down thumbnail row.
                const y = (maxCy - c.cy) * chunkPx;
                ctx.drawImage(bitmap, x, y, chunkPx, chunkPx);
            }
            ctx.globalAlpha = 1;
        }

        this.thumbBusy = true;
        this.lastThumbAt = Date.now();
        try {
            const blob: Blob | null = await new Promise((res) => canvas.toBlob(res, 'image/png'));
            if (!blob) return;
            const file = new File([blob], `tilemap-${tilemapUuid}.png`, { type: 'image/png' });
            const asset = await tilemapClient.uploadImage(projectUuid, file);
            await tilemapClient.updateTilemap(projectUuid, tilemapUuid, { thumbnail_asset_uuid: asset.uuid });
            await catalogStore.getState().refresh(projectUuid);
        } catch {
            /* thumbnail is best-effort */
        } finally {
            this.thumbBusy = false;
        }
    }

    private attachInput(): void {
        this.canvas.addEventListener('pointerdown', this.onPointerDown);
        this.canvas.addEventListener('pointermove', this.onPointerMove);
        this.canvas.addEventListener('pointerup', this.onPointerUp);
        this.canvas.addEventListener('pointerleave', this.onPointerLeave);
        this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
        this.canvas.addEventListener('contextmenu', this.onContextMenu);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
    }

    private detachInput(): void {
        this.canvas.removeEventListener('pointerdown', this.onPointerDown);
        this.canvas.removeEventListener('pointermove', this.onPointerMove);
        this.canvas.removeEventListener('pointerup', this.onPointerUp);
        this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
        this.canvas.removeEventListener('wheel', this.onWheel);
        this.canvas.removeEventListener('contextmenu', this.onContextMenu);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
    }
}
